import React, { useState } from 'react';
import { formatContactEnquiry, markWhatsAppSent, logEnquiry } from '../../utils/contactForm';
import { saveContactEnquiry, updateEnquiryWhatsAppStatus, isFirebaseConfigured } from '../../middleware/firebaseMiddleware';
import { MailIcon } from '../MailIcon';
import { PhoneIcon } from '../PhoneIcon';
import { MapPinIcon } from '../MapPinIcon';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s+()-]+$/.test(formData.phone)) {
      newErrors.phone = 'Phone number is invalid';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      let enquiryData = formatContactEnquiry(formData);
      logEnquiry(enquiryData);

      let enquiryDocId = null;
      if (isFirebaseConfigured()) {
        enquiryDocId = await saveContactEnquiry(enquiryData);
      } else {
        console.warn('⚠️ Firebase not configured. Enquiry will not be saved to database.');
      }

      // Generate WhatsApp message link
      const whatsappPhone = '918421717125';
      const whatsappMessage = `New Business Enquiry - Octopus Industries\n\n` +
        `Name: ${formData.name}\n` +
        `Company: ${formData.company || 'Not specified'}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone}\n\n` +
        `Message from customer:\n"${formData.message}"\n\n` +
        `This enquiry was submitted via the website contact form.`;
      
      const whatsappLink = `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(whatsappMessage)}`;

      await new Promise(resolve => setTimeout(resolve, 500));

      setSubmitStatus('success');

      enquiryData = markWhatsAppSent(enquiryData);

      if (isFirebaseConfigured() && enquiryDocId) {
        await updateEnquiryWhatsAppStatus(enquiryDocId, enquiryData);
      }

      window.open(whatsappLink, '_blank');
      
      // Clear form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="contact-header">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-description">
            Have a project in mind? Let&apos;s discuss how we can help with your packaging needs.
          </p>
        </div>

        <div className="contact-wrapper">
          <div className="contact-info">
            <div className="info-item">
              <div className="info-icon"><MailIcon size={24} /></div>
              <div className="info-content">
                <h4>Email</h4>
                <p>
                  <a href="mailto:sales@octopusindustries.in">sales@octopusindustries.in</a>
                </p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon"><PhoneIcon size={24} /></div>
              <div className="info-content">
                <h4>Phone</h4>
                <p>
                  <a href="tel:+918421717125">+91 8421717125</a><br/>
                  Mon-Fri: 8:00 AM - 6:00 PM
                </p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon"><MapPinIcon size={24} /></div>
              <div className="info-content">
                <h4>Address</h4>
                <p>
                  Yashwant Nagar, Talegaon Dabhade<br/>
                  Pune – 410507, Maharashtra, India
                </p>
              </div>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="company">Company Name</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>

            <div className="form-group full-width">
              <label htmlFor="message">Your Message *</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                className={errors.message ? 'error' : ''}
              ></textarea>
              {errors.message && <span className="error-message">{errors.message}</span>}
            </div>

            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="status-message success">
                Thank you! Your message has been sent successfully.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="status-message error">
                Oops! Something went wrong. Please try again.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
